import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import {
  History,
  Send,
  ShieldCheck,
  ShieldX,
  CheckCircle2,
  X,
  FileText,
  Snowflake,
  Loader2,
  Eye,
} from "lucide-react";

const EVENT_META = {
  "signature_request.created":   { icon: Send,          cls: "text-blue-700",    label: "Solicitud creada" },
  "signature_request.cancelled": { icon: X,             cls: "text-red-700",     label: "Solicitud cancelada" },
  "signature.portal_opened":     { icon: Eye,           cls: "text-neutral-600", label: "Enlace abierto" },
  "signature.pdf_previewed":     { icon: FileText,      cls: "text-neutral-600", label: "PDF previsualizado" },
  "signature.otp_sent":          { icon: Send,          cls: "text-blue-700",    label: "OTP enviado" },
  "signature.otp_verified":      { icon: ShieldCheck,   cls: "text-indigo-700",  label: "OTP verificado" },
  "signature.otp_failed":        { icon: ShieldX,       cls: "text-red-700",     label: "OTP incorrecto" },
  "signature.completed":         { icon: CheckCircle2,  cls: "text-emerald-700", label: "Firma realizada" },
  "contract.frozen":             { icon: Snowflake,     cls: "text-cyan-700",    label: "Contrato congelado" },
};

function fmt(dt) {
  try {
    const d = new Date(dt);
    return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" });
  } catch { return dt; }
}

export default function SignatureAuditDialog({ open, contract, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!contract?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${contract.id}/audit`);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [contract?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="signature-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-orange-500" />
            Trazabilidad — Contrato Nº {contract?.numero}
          </DialogTitle>
          <DialogDescription>
            Historial cronológico de todos los eventos registrados sobre este contrato: solicitudes, aperturas, verificación OTP, firmas y congelado.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-neutral-500 flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando eventos…
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-500 text-center">
            Aún no hay eventos registrados para este contrato.
          </div>
        ) : (
          <ol className="space-y-2">
            {events.map((e) => {
              const meta = EVENT_META[e.event] || { icon: FileText, cls: "text-neutral-600", label: e.event };
              const Ic = meta.icon;
              return (
                <li
                  key={e.id}
                  className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2"
                  data-testid={`audit-event-${e.id}`}
                >
                  <div className={`shrink-0 mt-0.5 ${meta.cls}`}>
                    <Ic className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${meta.cls}`}>{meta.label}</span>
                      <span className="text-[10px] text-neutral-400 font-mono">{fmt(e.at)}</span>
                    </div>
                    <div className="text-xs text-neutral-600 truncate">
                      <b>{e.actor || "—"}</b>
                      {e.ip && <> · IP {e.ip}</>}
                    </div>
                    {e.meta && Object.keys(e.meta).length > 0 && (
                      <div className="text-[10px] text-neutral-500 font-mono truncate mt-0.5">
                        {Object.entries(e.meta)
                          .filter(([, v]) => v !== "" && v != null)
                          .map(([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 24) : String(v)}`)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex justify-end pt-3 border-t border-neutral-100">
          <button type="button" onClick={onClose} className="btn-secondary">Cerrar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
